/**
 * Recipe catalog — honest availability, fixed semantics (audit F-04).
 *
 * Sections answer distinct questions and never blur:
 *   1. Bindable in this deployment  — shipped AND within the rollout ceiling.
 *   2. Shipped, later phase         — code-complete but gated by the
 *      deployment ceiling; the reason is printed inline, not as a post-hoc
 *      error after a click.
 *   3. Coming later                 — not yet shipped (available=false).
 *   4. Declined                     — defects this platform will not inspect
 *      optically, with the sensor that would be required.
 */
import { api } from '../api/client';
import { useApi } from '../hooks/useApi';
import type { Me, Recipe, RolloutPhase } from '../api/types';
import { Card, Empty, ErrorBox, Loading, Tag } from '../components/Metric';

const PHASE_ORDER: Record<RolloutPhase, number> = {
  PHASE_1: 1,
  PHASE_2: 2,
  PHASE_3: 3,
  PHASE_4: 4,
};

const PHASE_LABEL: Record<string, string> = {
  PHASE_1: 'Phase 1 — shipping',
  PHASE_2: 'Phase 2 — next',
  PHASE_3: 'Phase 3 — later',
  PHASE_4: 'Phase 4 — research',
};

function RecipeCard({ r, note }: { r: Recipe; note?: string }) {
  return (
    <Card
      title={r.name}
      actions={<Tag kind="advisory">Advisory</Tag>}
    >
      <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 0 }}>{r.description}</p>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0' }}>
        <Tag kind="brand">{PHASE_LABEL[r.rolloutPhase] ?? r.rolloutPhase}</Tag>
        <Tag>{r.processTypes.join(', ')}</Tag>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 6 }}>
        <strong style={{ color: 'var(--ink)' }}>Defects covered:</strong>{' '}
        {r.defectNamesSubset.join(', ')}
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
        <strong style={{ color: 'var(--ink)' }}>Permitted actions:</strong>{' '}
        <span className="mono">{r.permittedActions.join(', ')}</span>
      </div>
      {note && <div className="card-note">{note}</div>}
      {r.entitlement && !r.entitlement.entitled && (
        <div className="callout warn" style={{ marginTop: 12, marginBottom: 0 }}>
          <strong>Not in your plan</strong>
          {r.entitlement.reason}
          {r.entitlement.marketplaceUrl && (
            <>
              {' '}
              <a href={r.entitlement.marketplaceUrl}>Manage in the marketplace</a>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

export function RecipesPage({ me }: { me: Me }) {
  const recipes = useApi<Recipe[]>(() => api.get<Recipe[]>('/api/v1/recipes'), []);
  const declines = useApi<{
    message: string;
    defects: Array<{ defectName: string; why: string; requiredSensor: string; ingestionPath: string }>;
  }>(() => api.get('/api/v1/recipes/non-optical-declines'), []);

  if (recipes.loading) return <Loading label="Loading recipes…" />;
  if (recipes.error) return <ErrorBox message={recipes.error} />;

  const ceiling = me.rolloutCeiling ?? 'PHASE_1';
  const within = (p: RolloutPhase) => PHASE_ORDER[p] <= PHASE_ORDER[ceiling as RolloutPhase];

  const bindable = recipes.data?.filter((r) => r.available && within(r.rolloutPhase)) ?? [];
  const laterPhase = recipes.data?.filter((r) => r.available && !within(r.rolloutPhase)) ?? [];
  const coming = recipes.data?.filter((r) => !r.available) ?? [];

  return (
    <>
      <div className="callout info">
        <strong>Every recipe is advisory</strong>
        No recipe in this catalog issues a signal that arrests safety-rated
        machine motion. Permitted actions are limited to advisory flags, SPC
        logging, alerts, and a non-safety-rated 24 V reject gate.
      </div>

      <h2 style={{ marginBottom: 12 }}>Bindable in this deployment</h2>
      <p className="sub" style={{ marginTop: 0, marginBottom: 12, color: 'var(--ink-2)' }}>
        Shipped and within this deployment&rsquo;s rollout ceiling ({ceiling}).
        These can be bound to a camera station today.
      </p>
      {bindable.length === 0 ? (
        <Empty title="Nothing bindable yet">
          No shipped recipe is inside the rollout ceiling of this deployment.
        </Empty>
      ) : (
        <div className="grid grid-2">
          {bindable.map((r) => (
            <RecipeCard key={r.id} r={r} />
          ))}
        </div>
      )}

      {laterPhase.length > 0 && (
        <>
          <h2 style={{ margin: '26px 0 12px' }}>Shipped in a later rollout phase</h2>
          <p style={{ color: 'var(--ink-2)', marginTop: 0, marginBottom: 12 }}>
            The software exists, but this deployment exposes up to {ceiling}.
            The reason is stated here so nobody has to click to discover it.
          </p>
          <div className="grid grid-2">
            {laterPhase.map((r) => (
              <RecipeCard
                key={r.id}
                r={r}
                note={`Not bindable here: ships in ${r.rolloutPhase}; this deployment's ceiling is ${ceiling}. ${r.comingSoonNote ?? ''}`}
              />
            ))}
          </div>
        </>
      )}

      {coming.length > 0 && (
        <>
          <h2 style={{ margin: '26px 0 12px' }}>Coming later</h2>
          <p style={{ color: 'var(--ink-2)', marginTop: 0, marginBottom: 12 }}>
            These exist as configuration, not as hidden code. They are shown with
            their intended phase so a roadmap conversation has something concrete
            to point at.
          </p>
          <div className="grid grid-2">
            {coming.map((r) => (
              <Card
                key={r.id}
                title={r.name}
                actions={<Tag>{PHASE_LABEL[r.rolloutPhase] ?? r.rolloutPhase}</Tag>}
              >
                <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 0 }}>{r.description}</p>
                <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 8 }}>
                  <strong style={{ color: 'var(--ink)' }}>Intended defects:</strong>{' '}
                  {r.defectNamesSubset.join(', ')}
                </div>
                {r.comingSoonNote && <div className="card-note">{r.comingSoonNote}</div>}
              </Card>
            ))}
          </div>
        </>
      )}

      <div style={{ marginTop: 26 }}>
        <Card title="Defects this platform declines to inspect optically" note={declines.data?.message}>
          {!declines.data || declines.data.defects.length === 0 ? (
            <Empty title="No recorded declines" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Defect</th>
                    <th>Why a camera cannot see it</th>
                    <th>Sensor actually required</th>
                    <th>What to do instead</th>
                  </tr>
                </thead>
                <tbody>
                  {declines.data.defects.map((d) => (
                    <tr key={d.defectName}>
                      <td style={{ fontWeight: 650 }}>{d.defectName}</td>
                      <td style={{ color: 'var(--ink-2)' }}>{d.why}</td>
                      <td>
                        <Tag kind="warn">{d.requiredSensor}</Tag>
                      </td>
                      <td style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>{d.ingestionPath}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="card-note">
            This is a scope statement, not a gap. Pretending a visible-light
            camera can detect a subsurface crack is the kind of claim that gets
            a quality system trusted and then discarded.
          </div>
        </Card>
      </div>
    </>
  );
}
